import React, { useState } from 'react';
import { Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/contexts/AuthContext';
import { Eye, EyeOff, Lock, AlertCircle, ArrowLeft } from 'lucide-react';
import { motion } from 'framer-motion';
import { ROUTES } from '@/config/routes';
import { useMarketingTranslations } from '@/hooks/useMarketingTranslations';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [formErrors, setFormErrors] = useState<{
    email?: string;
    password?: string;
  }>({});
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { login, isAuthenticated, loading } = useAuth();
  const {
    getLoginText,
    getLoginBranding,
    getLoginForm,
    getLoginField,
    getLoginButton,
    getLoginError,
    getLoginSignup,
    getLoginSecurity,
    getLoginFooter,
  } = useMarketingTranslations();

  // Redirect authenticated users away from login page
  React.useEffect(() => {
    if (!loading && isAuthenticated) {
      const redirectTo = location.state?.from?.pathname || '/app';
      navigate(redirectTo, { replace: true });
    }
  }, [isAuthenticated, loading, navigate, location.state?.from?.pathname]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormErrors({});
    setError('');

    // Basic validation
    const errors: { email?: string; password?: string } = {};
    if (!email.trim()) {
      errors.email = getLoginField('email', 'required');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errors.email = getLoginField('email', 'invalid');
    }

    if (!password.trim()) {
      errors.password = getLoginField('password', 'required');
    } else if (password.length < 6) {
      errors.password = getLoginField('password', 'minLength');
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setIsLoading(true);
    try {
      await login(email, password);
      const redirectTo = location.state?.from?.pathname || '/app';
      navigate(redirectTo, { replace: true });
    } catch (err: any) {
      setError(err.message || getLoginError());
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className="flex min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900"
      data-scroll-container
    >
      {/* Back Button - Fixed positioned at top left */}
      <motion.div
        className="fixed left-6 top-6 z-50"
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.6 }}
      >
        <Link
          to="/"
          className="group flex items-center gap-2 rounded-lg border border-slate-700/50 bg-slate-800/80 px-4 py-2 text-slate-300 shadow-lg backdrop-blur-sm transition-all duration-200 hover:bg-slate-700/80 hover:text-white hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-slate-900"
          onClick={() => {
            setTimeout(() => {
              window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
              document.documentElement.scrollTop = 0;
              document.body.scrollTop = 0;
            }, 0);
          }}
        >
          <ArrowLeft className="size-4 transition-transform group-hover:-translate-x-1" />
          <span className="text-sm font-medium">{getLoginText('backToHome')}</span>
        </Link>
      </motion.div>

      {/* Left side - Branding */}
      <div className="relative hidden overflow-hidden lg:flex lg:w-1/2">
        <div className="absolute inset-0 bg-gradient-to-br from-primary-600/20 to-primary-500/10 backdrop-blur-sm" />
        <div className="relative z-10 flex flex-col justify-center px-12 py-16">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="mb-8 flex items-center">
              <img
                src="/kernlogic/kernlogic-logo-icon.svg"
                alt={getLoginText('brand')}
                className="size-16"
              />
              <span className="ml-4 text-3xl font-bold leading-[1.2] text-white">
                {getLoginText('brand')}
              </span>
            </div>
            <h1 className="mb-6 text-4xl font-bold leading-[1.2] text-white">
              {getLoginBranding('title')}
            </h1>
            <p className="text-xl leading-relaxed text-slate-300">{getLoginBranding('subtitle')}</p>
          </motion.div>
        </div>
      </div>

      {/* Right side - Login Form */}
      <div className="flex w-full items-center justify-center px-6 py-12 lg:w-1/2">
        <motion.div
          className="w-full max-w-md"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="rounded-2xl border border-slate-700/50 bg-gradient-to-br from-slate-800/90 to-slate-900/90 p-8 shadow-2xl backdrop-blur-sm">
            {/* Header */}
            <div className="mb-8 text-center">
              <motion.div
                className="mb-6 flex items-center justify-center lg:hidden"
                initial={{ y: -20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <img
                  src="/kernlogic/kernlogic-logo-icon.svg"
                  alt={getLoginText('brand')}
                  className="size-12"
                />
                <span className="ml-3 text-xl font-bold leading-[1.2] text-white">
                  {getLoginText('brand')}
                </span>
              </motion.div>
              <motion.h1
                className="mb-2 text-2xl font-bold leading-[1.3] text-white"
                initial={{ y: -10, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.3 }}
              >
                {getLoginForm('title')}
              </motion.h1>
              <motion.p
                className="text-slate-400"
                initial={{ y: 10, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.4 }}
              >
                {getLoginForm('subtitle')}
              </motion.p>
            </div>

            {/* Message Display */}
            {(() => {
              const message = searchParams.get('message');
              if (message === 'invitation_already_used') {
                return (
                  <motion.div
                    className="mb-6 flex items-center rounded-lg border border-blue-500/20 bg-blue-500/10 p-4"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    role="alert"
                    aria-live="polite"
                  >
                    <AlertCircle className="mr-3 size-5 shrink-0 text-blue-400" />
                    <span className="text-sm text-blue-300">
                      {getLoginText('messages.invitationAlreadyUsed')}
                    </span>
                  </motion.div>
                );
              } else if (message === 'organization_created') {
                return (
                  <motion.div
                    className="mb-6 flex items-center rounded-lg border border-green-500/20 bg-green-500/10 p-4"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    role="alert"
                    aria-live="polite"
                  >
                    <AlertCircle className="mr-3 size-5 shrink-0 text-green-400" />
                    <span className="text-sm text-green-300">
                      {getLoginText('messages.organizationCreated')}
                    </span>
                  </motion.div>
                );
              }
              return null;
            })()}

            {/* Error Display */}
            {error && (
              <motion.div
                className="mb-6 flex items-center rounded-lg border border-red-500/20 bg-red-500/10 p-4"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                role="alert"
                aria-live="polite"
              >
                <AlertCircle className="mr-3 size-5 shrink-0 text-red-400" />
                <span className="text-sm text-red-300">{error}</span>
              </motion.div>
            )}

            <form onSubmit={handleSubmit} noValidate>
              <div className="space-y-6">
                {/* Email Field */}
                <motion.div
                  className="relative"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.5 }}
                >
                  <div className="relative">
                    <label
                      htmlFor="email"
                      className="mb-1 block text-sm font-medium text-slate-400"
                    >
                      {getLoginField('email', 'label')}
                    </label>
                    <Input
                      type="email"
                      id="email"
                      name="email"
                      value={email}
                      placeholder={getLoginField('email', 'label')}
                      required
                      onChange={e => setEmail(e.target.value)}
                      className={`w-full rounded-lg border bg-slate-800/50 px-4 py-3 text-white backdrop-blur-sm transition-colors focus:outline-none ${
                        formErrors.email
                          ? 'border-red-500 focus:border-red-500'
                          : 'border-slate-600 focus:border-primary-500'
                      }`}
                      aria-describedby={formErrors.email ? 'email-error' : undefined}
                    />
                  </div>
                  {formErrors.email && (
                    <p id="email-error" className="mt-2 flex items-center text-sm text-red-400">
                      <AlertCircle className="mr-1 size-4" />
                      {formErrors.email}
                    </p>
                  )}
                </motion.div>

                {/* Password Field */}
                <motion.div
                  className="relative"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.6 }}
                >
                  <div className="relative">
                    <label
                      htmlFor="password"
                      className="mb-1 block text-sm font-medium text-slate-400"
                    >
                      {getLoginField('password', 'label')}
                    </label>
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      id="password"
                      name="password"
                      value={password}
                      placeholder={getLoginField('password', 'label')}
                      required
                      onChange={e => setPassword(e.target.value)}
                      className={`w-full rounded-lg border bg-slate-800/50 px-4 py-3 pr-12 text-white backdrop-blur-sm transition-colors focus:outline-none ${
                        formErrors.password
                          ? 'border-red-500 focus:border-red-500'
                          : 'border-slate-600 focus:border-primary-500'
                      }`}
                      aria-describedby={formErrors.password ? 'password-error' : undefined}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-10 text-slate-400 transition-colors hover:text-primary-400 focus:text-primary-400 focus:outline-none"
                      aria-label={
                        showPassword ? getLoginForm('hidePassword') : getLoginForm('showPassword')
                      }
                    >
                      {showPassword ? <EyeOff className="size-5" /> : <Eye className="size-5" />}
                    </button>
                  </div>
                  {formErrors.password && (
                    <p id="password-error" className="mt-2 flex items-center text-sm text-red-400">
                      <AlertCircle className="mr-1 size-4" />
                      {formErrors.password}
                    </p>
                  )}
                </motion.div>
              </div>

              {/* Forgot Password */}
              <motion.div
                className="mt-4 text-right"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.7 }}
              >
                <Link
                  to={ROUTES.AUTH.FORGOT_PASSWORD}
                  className="rounded-md px-1 text-sm text-primary-400 transition-colors hover:text-primary-300 hover:underline focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-slate-900"
                >
                  {getLoginForm('forgotPassword')}
                </Link>
              </motion.div>

              {/* Submit Button */}
              <motion.div
                className="mt-8"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.8 }}
              >
                <Button
                  type="submit"
                  disabled={isLoading}
                  className="w-full rounded-lg bg-gradient-to-r from-primary-600 to-primary-500 py-3 font-semibold text-white shadow-lg transition-all duration-200 hover:from-primary-700 hover:to-primary-600 hover:shadow-xl focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-slate-900 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {isLoading ? (
                    <div className="flex items-center justify-center">
                      <div className="mr-2 size-5 animate-spin rounded-full border-b-2 border-white"></div>
                      {getLoginButton('signingIn')}
                    </div>
                  ) : (
                    getLoginButton('signIn')
                  )}
                </Button>
              </motion.div>
            </form>

            {/* Sign Up Link */}
            <motion.div
              className="mt-6 text-center"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.6 }}
            >
              <p className="text-sm text-slate-400">
                {getLoginSignup('text')}{' '}
                <Link
                  to="/contact"
                  className="rounded-md px-1 font-semibold text-primary-400 transition-colors hover:text-primary-300 hover:underline focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-slate-900"
                >
                  {getLoginSignup('link')}
                </Link>
              </p>
            </motion.div>

            {/* Security Notice */}
            <motion.div
              className="mt-8 border-t border-slate-700/50 pt-6 text-center"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.9 }}
            >
              <div className="flex items-center justify-center text-sm text-slate-500">
                <Lock className="mr-2 size-4" />
                <span>{getLoginSecurity('text')}</span>
              </div>
            </motion.div>

            {/* Footer Links */}
            <motion.div
              className="mt-6 text-center text-sm text-slate-500"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 1.0 }}
            >
              <Link
                to={ROUTES.AUTH.PRIVACY}
                className="rounded-md px-1 transition-colors hover:text-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-slate-900"
              >
                {getLoginFooter('privacy')}
              </Link>
              <span className="mx-2">•</span>
              <Link
                to={ROUTES.AUTH.TERMS}
                className="rounded-md px-1 transition-colors hover:text-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-slate-900"
              >
                {getLoginFooter('terms')}
              </Link>
              <span className="mx-2">•</span>
              <Link
                to="/support"
                className="rounded-md px-1 transition-colors hover:text-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-slate-900"
              >
                {getLoginFooter('support')}
              </Link>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
